import {
  type AvailableModelList,
  AvailableModelListSchema,
  type LlmProvider,
  type ModelTier,
  type ModelTierBinding,
  ModelTierBindingListSchema,
  ModelTierBindingSchema,
} from '@sbc/domain-models/agents';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet, apiSend } from './client';

const MODEL_TIERS_KEY = ['admin', 'model-tiers'];

export function useModelTiers() {
  return useQuery<ModelTierBinding[]>({
    queryKey: MODEL_TIERS_KEY,
    queryFn: () => apiGet('/api/admin/model-tiers', ModelTierBindingListSchema),
  });
}

// Keyed by provider: each provider serves its own catalog, so the two lists must never
// share a cache entry.
export function useAvailableModels(provider: LlmProvider) {
  return useQuery<AvailableModelList>({
    queryKey: [...MODEL_TIERS_KEY, 'available', provider],
    queryFn: () =>
      apiGet(`/api/admin/model-tiers/available?provider=${provider}`, AvailableModelListSchema),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateModelTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tier,
      provider,
      modelId,
    }: {
      tier: ModelTier;
      provider: LlmProvider;
      modelId: string;
    }) =>
      apiSend(
        'PUT',
        `/api/admin/model-tiers/${tier}`,
        { provider, modelId },
        ModelTierBindingSchema,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MODEL_TIERS_KEY }),
  });
}
